import { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import {
    Plus, Edit, Trash2, Search, Download, UserRound, ChevronLeft, ChevronRight,
    KeyRound, MoreHorizontal, Shield, ArrowUpDown,
} from 'lucide-react';

const AVATAR_COLORS = ['#f59e0b', '#8b5cf6', '#10b981', '#3b82f6', '#ec4899', '#14b8a6', '#f97316', '#6366f1'];

const initialsOf = (name) => (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const avatarColor = (name) => {
    const text = name || '';
    let hash = 0;
    for (let i = 0; i < text.length; i += 1) hash = (hash + text.charCodeAt(i)) % AVATAR_COLORS.length;
    return AVATAR_COLORS[hash];
};

const roleTone = (name) => {
    const n = (name || '').toLowerCase();
    if (n.includes('admin')) return { background: '#e7f0ff', color: '#2563eb' };
    if (n.includes('preventa') || n.includes('vendedor')) return { background: '#f3e8ff', color: '#7c3aed' };
    if (n.includes('cajero')) return { background: '#fff4d6', color: '#b45309' };
    return { background: '#f4f4f5', color: '#3f3f46' };
};

const formatAccess = (value) => {
    if (!value) return { when: 'Sin registro', ago: '' };
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return { when: 'Sin registro', ago: '' };
    const when = date.toLocaleString('es-CL', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone: 'America/Santiago',
    });
    const mins = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
    let ago = 'Hace un momento';
    if (mins >= 60 && mins < 60 * 24) {
        const hours = Math.floor(mins / 60);
        ago = `Hace ${hours} hora${hours === 1 ? '' : 's'}`;
    } else if (mins >= 60 * 24) {
        const days = Math.floor(mins / (60 * 24));
        ago = `Hace ${days} día${days === 1 ? '' : 's'}`;
    } else if (mins >= 1) {
        ago = `Hace ${mins} min`;
    }
    return { when, ago };
};

const isActiveUser = (user) => user.estado === true || user.estado === 'true' || user.estado === 1;

export default function UsersPage() {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '', email: '', password: '', tipo_usuario_id: '', estado: true,
    });
    const [editingId, setEditingId] = useState(null);
    const [query, setQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('todos');
    const [statusFilter, setStatusFilter] = useState('todos');
    const [sortBy, setSortBy] = useState('recientes');
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [menuUserId, setMenuUserId] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        try {
            const [uRes, rRes] = await Promise.all([
                fetch(`${API_URL}/api/users`, { headers }),
                fetch(`${API_URL}/api/users/roles`, { headers }),
            ]);
            const userData = await uRes.json();
            const roleData = await rRes.json();
            setUsers(Array.isArray(userData) ? userData : []);
            setRoles(Array.isArray(roleData) ? roleData : []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        let rows = users.filter((user) => {
            const roleName = user.role_name || '';
            const matchesQuery = !q
                || (user.name || '').toLowerCase().includes(q)
                || (user.email || '').toLowerCase().includes(q)
                || String(user.id).includes(q);
            const matchesRole = roleFilter === 'todos' || String(user.tipo_usuario_id) === roleFilter;
            const active = isActiveUser(user);
            const matchesStatus = statusFilter === 'todos'
                || (statusFilter === 'activo' && active)
                || (statusFilter === 'inactivo' && !active);
            return matchesQuery && matchesRole && matchesStatus;
        });
        rows = [...rows].sort((a, b) => {
            if (sortBy === 'nombre') return (a.name || '').localeCompare(b.name || '', 'es');
            if (sortBy === 'id') return Number(a.id) - Number(b.id);
            const ta = a.ultimo_acceso ? new Date(a.ultimo_acceso).getTime() : 0;
            const tb = b.ultimo_acceso ? new Date(b.ultimo_acceso).getTime() : 0;
            return tb - ta || Number(b.id) - Number(a.id);
        });
        return rows;
    }, [users, query, roleFilter, statusFilter, sortBy]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, pageCount);
    const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
    const from = filtered.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const to = Math.min(safePage * pageSize, filtered.length);
    const pageIds = pageRows.map((user) => user.id);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));

    const toggleAllPage = () => {
        setSelected((prev) => (
            allPageSelected
                ? prev.filter((id) => !pageIds.includes(id))
                : [...new Set([...prev, ...pageIds])]
        ));
    };

    const toggleOne = (id) => {
        setSelected((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
    };

    const saveUser = async (payload, id) => {
        const url = id ? `${API_URL}/api/users/${id}` : `${API_URL}/api/users`;
        const body = { ...payload };
        if (id && !body.password) delete body.password;
        const res = await fetch(url, {
            method: id ? 'PUT' : 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || 'No se pudo guardar el usuario');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await saveUser(formData, editingId);
            setModalOpen(false);
            setFormData({ name: '', email: '', password: '', tipo_usuario_id: '', estado: true });
            setEditingId(null);
            fetchData();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleDelete = async (id, name) => {
        if (!confirm(`¿Estás seguro de eliminar al usuario "${name}"? Esta acción no se puede deshacer.`)) return;
        try {
            const res = await fetch(`${API_URL}/api/users/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            if (res.ok) {
                setSelected((prev) => prev.filter((item) => item !== id));
                fetchData();
            } else {
                const data = await res.json();
                alert(data.error || 'Error al eliminar usuario');
            }
        } catch (err) {
            console.error(err);
            alert('Error al eliminar usuario');
        }
    };

    const setEstado = async (user, estado) => {
        await saveUser({
            name: user.name,
            email: user.email,
            tipo_usuario_id: user.tipo_usuario_id,
            estado,
        }, user.id);
    };

    const deactivateSelected = async () => {
        const targets = users.filter((user) => selected.includes(user.id) && isActiveUser(user));
        if (targets.length === 0) {
            alert('Selecciona al menos un usuario activo.');
            return;
        }
        if (!confirm(`¿Desactivar ${targets.length} usuario${targets.length === 1 ? '' : 's'}?`)) return;
        try {
            for (const user of targets) {
                await setEstado(user, false);
            }
            setSelected([]);
            fetchData();
        } catch (err) {
            alert(err.message || 'No se pudieron desactivar');
        }
    };

    const exportCsv = () => {
        const header = ['ID', 'Nombre', 'Email', 'Rol', 'Estado', 'Ultimo acceso'];
        const lines = filtered.map((user) => [
            user.id,
            `"${(user.name || '').replace(/"/g, '""')}"`,
            user.email || '',
            user.role_name || '',
            isActiveUser(user) ? 'Activo' : 'Inactivo',
            user.ultimo_acceso || '',
        ].join(','));
        const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'usuarios.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    const openEdit = (user) => {
        setFormData({
            name: user.name,
            email: user.email,
            tipo_usuario_id: user.tipo_usuario_id,
            estado: isActiveUser(user),
            password: '',
        });
        setEditingId(user.id);
        setMenuUserId(null);
        setModalOpen(true);
    };

    const openCreate = () => {
        setEditingId(null);
        setFormData({ name: '', email: '', password: '', tipo_usuario_id: '', estado: true });
        setModalOpen(true);
    };

    return (
        <div className="users-screen">
            <div className="users-head">
                <div className="users-title">
                    <span className="users-title-icon" aria-hidden><UserRound size={22} /></span>
                    <div>
                        <h1>Gestión de Usuarios</h1>
                        <p>Administra accesos, roles y estados del equipo.</p>
                    </div>
                </div>
                <button type="button" className="users-new" onClick={openCreate}>
                    <Plus size={18} /> Nuevo Usuario
                </button>
            </div>

            <div className="users-card">
                <div className="users-toolbar">
                    <label className="users-search">
                        <Search size={16} aria-hidden />
                        <input
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                            placeholder="Buscar usuarios por nombre, email..."
                        />
                    </label>
                    <label className="users-filter">
                        <UserRound size={15} aria-hidden />
                        <select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}>
                            <option value="todos">Todos los roles</option>
                            {roles.map((role) => (
                                <option key={role.id} value={role.id}>{role.nombre}</option>
                            ))}
                        </select>
                    </label>
                    <label className="users-filter">
                        <span className="users-dot" aria-hidden />
                        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                            <option value="todos">Todos los estados</option>
                            <option value="activo">Activo</option>
                            <option value="inactivo">Inactivo</option>
                        </select>
                    </label>
                    <label className="users-filter">
                        <ArrowUpDown size={15} aria-hidden />
                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                            <option value="recientes">Más recientes</option>
                            <option value="nombre">Nombre</option>
                            <option value="id">ID</option>
                        </select>
                    </label>
                    <button type="button" className="users-ghost" onClick={exportCsv}>
                        <Download size={16} /> Exportar
                    </button>
                    <button type="button" className="users-danger" onClick={deactivateSelected}>
                        <Trash2 size={15} /> Desactivar
                    </button>
                </div>

                {loading ? (
                    <p className="users-empty">Cargando usuarios...</p>
                ) : (
                    <div className="users-table-wrap">
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>
                                        <input type="checkbox" checked={allPageSelected} onChange={toggleAllPage} aria-label="Seleccionar página" />
                                    </th>
                                    <th>Usuario</th>
                                    <th>Email</th>
                                    <th>Rol</th>
                                    <th>Estado</th>
                                    <th>Último acceso</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageRows.map((user) => {
                                    const access = formatAccess(user.ultimo_acceso);
                                    const tone = roleTone(user.role_name);
                                    const active = isActiveUser(user);
                                    return (
                                        <tr key={user.id}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selected.includes(user.id)}
                                                    onChange={() => toggleOne(user.id)}
                                                    aria-label={`Seleccionar ${user.name}`}
                                                />
                                            </td>
                                            <td>
                                                <div className="users-person">
                                                    <span className="users-avatar" style={{ background: avatarColor(user.name) }}>
                                                        {initialsOf(user.name)}
                                                    </span>
                                                    <span>
                                                        <strong>{user.name}</strong>
                                                        <small>ID: {user.id}</small>
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="users-email">{user.email}</td>
                                            <td>
                                                <span className="users-role" style={tone}>
                                                    <Shield size={13} aria-hidden />
                                                    {user.role_name || 'Sin rol'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`users-status ${active ? 'is-on' : 'is-off'}`}>
                                                    {active ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="users-access">
                                                    <strong>{access.when}</strong>
                                                    {access.ago && <small>{access.ago}</small>}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="users-actions">
                                                    <button type="button" className="is-edit" title="Editar" onClick={() => openEdit(user)}><Edit size={16} /></button>
                                                    <button type="button" className="is-key" title="Cambiar contraseña" onClick={() => openEdit(user)}><KeyRound size={16} /></button>
                                                    <button type="button" title="Eliminar" className="is-danger" onClick={() => handleDelete(user.id, user.name)}><Trash2 size={16} /></button>
                                                    <div className="users-more">
                                                        <button type="button" title="Más" onClick={() => setMenuUserId(menuUserId === user.id ? null : user.id)}>
                                                            <MoreHorizontal size={16} />
                                                        </button>
                                                        {menuUserId === user.id && (
                                                            <button
                                                                type="button"
                                                                className="users-more-menu"
                                                                onClick={async () => {
                                                                    setMenuUserId(null);
                                                                    try {
                                                                        await setEstado(user, !active);
                                                                        fetchData();
                                                                    } catch (err) {
                                                                        alert(err.message);
                                                                    }
                                                                }}
                                                            >
                                                                {active ? 'Desactivar' : 'Activar'}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {pageRows.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="users-empty">No hay usuarios con esos filtros.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="users-footer">
                    <span>Mostrando {from} a {to} de {filtered.length} usuarios</span>
                    <div className="users-pager">
                        <label>
                            Filas por página:
                            <select
                                value={pageSize}
                                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                            >
                                {[10, 20, 50].map((size) => <option key={size} value={size}>{size}</option>)}
                            </select>
                        </label>
                        <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1} aria-label="Página anterior">
                            <ChevronLeft size={16} />
                        </button>
                        {Array.from({ length: pageCount }, (_, index) => index + 1).slice(0, 6).map((n) => (
                            <button
                                key={n}
                                type="button"
                                className={n === safePage ? 'is-current' : ''}
                                onClick={() => setPage(n)}
                            >
                                {n}
                            </button>
                        ))}
                        <button type="button" onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={safePage === pageCount} aria-label="Página siguiente">
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h2 className="modal-title">{editingId ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
                        <form onSubmit={handleSubmit} className="form-grid">
                            <div className="form-group full-width">
                                <label>Nombre</label>
                                <input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                            </div>
                            <div className="form-group full-width">
                                <label>Email</label>
                                <input required type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                            </div>
                            <div className="form-group full-width">
                                <label>{editingId ? 'Nueva contraseña (opcional)' : 'Contraseña'}</label>
                                <input
                                    required={!editingId}
                                    type="password"
                                    minLength={editingId ? undefined : 6}
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                />
                            </div>
                            <div className="form-group full-width">
                                <label>Rol</label>
                                <select
                                    required
                                    value={formData.tipo_usuario_id}
                                    onChange={(e) => setFormData({ ...formData, tipo_usuario_id: e.target.value })}
                                >
                                    <option value="">Seleccionar Rol</option>
                                    {roles.map((role) => (
                                        <option key={role.id} value={role.id}>{role.nombre}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-actions">
                                <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
